"use client";

import { useState, type ClipboardEvent, type RefObject } from "react";

const CIFRAS = 6;

interface CodigoSeisCifrasProps {
  id: string;
  valor: string;
  /** Siempre recibe solo cifras, como mucho seis. */
  onCambio: (valor: string) => void;
  /** Una vez por cada valor que llega a seis cifras (tecleado, pegado o autocompletado). */
  onCompleto: (valor: string) => void;
  etiqueta: string;
  describedBy?: string;
  deshabilitado?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
}

function limpiar(texto: string): string {
  return texto.replace(/\D/g, "").slice(0, CIFRAS);
}

/**
 * El código de 6 cifras de la pantalla de acceso (UI spec 003, S1-b y S1-c).
 *
 * **Es un solo `<input>` de verdad**, transparente encima de seis casillas que
 * solo se ven. Seis inputs rompen el pegado, el autocompletado del código que
 * llega por SMS o correo en iOS y Android, y la lectura del lector de
 * pantalla; uno solo no. Las casillas son `aria-hidden`: el nombre y el valor
 * los lleva el input.
 */
export default function CodigoSeisCifras({
  id,
  valor,
  onCambio,
  onCompleto,
  etiqueta,
  describedBy,
  deshabilitado = false,
  error = false,
  autoFocus = false,
  inputRef,
}: CodigoSeisCifrasProps) {
  const [enfocado, setEnfocado] = useState(false);

  function aplicar(texto: string): void {
    const limpio = limpiar(texto);
    if (limpio === valor) return;
    onCambio(limpio);
    if (limpio.length === CIFRAS) onCompleto(limpio);
  }

  // «123 456» o «123-456» miden 7: con `maxLength` el navegador cortaría el
  // pegado antes de limpiarlo y se perdería la última cifra.
  function pegar(e: ClipboardEvent<HTMLInputElement>): void {
    e.preventDefault();
    aplicar(e.clipboardData.getData("text"));
  }

  // Con el cursor siempre al final, «Borrar» quita la última cifra aunque se
  // haya tocado encima de otra casilla.
  function cursorAlFinal(input: HTMLInputElement): void {
    const fin = input.value.length;
    input.setSelectionRange(fin, fin);
  }

  const activa = enfocado && !deshabilitado ? Math.min(valor.length, CIFRAS - 1) : -1;

  return (
    <div className="relative mx-auto w-fit">
      <div aria-hidden="true" className="flex justify-center gap-1.5 sm:gap-3">
        {Array.from({ length: CIFRAS }, (_, i) => {
          const cifra = valor[i] ?? "";
          const estado = error
            ? "border-red-500 bg-red-50"
            : i === activa
              ? "border-brand bg-white ring-2 ring-brand-focus"
              : "border-line-strong bg-white";
          return (
            <div
              key={i}
              className={[
                "flex h-14 w-10 items-center justify-center rounded-xl border text-center font-mono text-2xl font-semibold tabular-nums text-zinc-900 sm:h-16 sm:w-12",
                estado,
                deshabilitado ? "opacity-60" : "",
              ].join(" ")}
            >
              {cifra}
            </div>
          );
        })}
      </div>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={CIFRAS}
        value={valor}
        onChange={(e) => aplicar(e.target.value)}
        onPaste={pegar}
        onFocus={(e) => {
          setEnfocado(true);
          cursorAlFinal(e.currentTarget);
        }}
        onClick={(e) => cursorAlFinal(e.currentTarget)}
        onBlur={() => setEnfocado(false)}
        aria-label={etiqueta}
        aria-describedby={describedBy}
        aria-invalid={error}
        disabled={deshabilitado}
        autoFocus={autoFocus}
        spellCheck={false}
        className="absolute inset-0 h-full w-full cursor-text text-base caret-transparent opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
}
